import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Divider,
  IconButton,
} from '@mui/material';
import { ArrowBack, Save, CategoryOutlined } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../services/api';
import { useNotification } from '../../contexts/NotificationContext';
import { Category } from '../../types/api';

interface CategoryFormProps {
  mode?: 'create' | 'edit';
}

const CategoryForm: React.FC<CategoryFormProps> = ({ mode = 'create' }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { showSuccess, showError, showWarning } = useNotification();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
  });

  // Fetch existing category for edit mode
  useEffect(() => {
    if (mode === 'edit' && id) {
      api.get(`/api/categories/${id}`)
        .then((res) => {
          const category: Category = res.data;
          setFormData({
            name: category.name || '',
          });
        })
        .catch((err) => {
          console.error(err);
          showError('Failed to load category details');
          navigate(-1);
        });
    }
  }, [mode, id, navigate]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (!formData.name.trim()) {
      showWarning('Category name is required');
      return;
    }

    const payload = {
      name: formData.name.trim(),
    };

    setLoading(true);
    try {
      if (mode === 'edit' && id) {
        await api.put(`/api/categories/${id}`, payload);
        showSuccess('Category updated successfully!');
        navigate(-1);
      } else {
        await api.post('/api/categories', payload);
        showSuccess('Category created successfully!');
        navigate('/categories');
      }
    } catch (err: any) {
      console.error(err);
      showError(err.message|| 'Failed to save category');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
          <ArrowBack />
        </IconButton>
        <CategoryOutlined sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
        <Typography variant="h5" fontWeight="bold">
          {mode === 'edit' ? 'Edit Category' : 'Add New Category'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Category Name */}
            <Grid size={12}>
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="Enter category name"
                inputProps={{ maxLength: 100 }}
                helperText="Maximum 100 characters"
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* Action Buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button variant="outlined" onClick={() => navigate(-1)} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={<Save />}
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'edit' ? 'Update Category' : 'Create Category'}
            </Button>
          </Box>
        </form>
      </Paper>
    </Box>
  );
};

export default CategoryForm;
